import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import HelpOutline from '@mui/icons-material/HelpOutline';
import { Alert, Box, Button, Chip, FormControl, Grid, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';

import {
  evaluateGuidedMode,
  getGuidedModeDefaults,
  getGuidedQuestionsForDisplay,
  GuidedModeAnswers,
  GuidedModePath,
  GuidedModeRecommendation,
} from '../../lib/services/guidedMode';
import type { MarketplaceService } from '../../types/pricing';

interface GuidedModePanelProps {
  service: MarketplaceService;
  // eslint-disable-next-line no-unused-vars
  onRecommendationChange(nextRecommendation: GuidedModeRecommendation): void;
}

const pathCards: Array<{ path: GuidedModePath; title: string; body: string; icon: React.ReactNode }> = [
  {
    path: 'standard_package',
    title: 'Standard Package',
    body: 'Use the default managed IT package and confirm environment details after checkout.',
    icon: <CheckCircleOutline />,
  },
  {
    path: 'help_me_choose',
    title: 'Help Me Choose',
    body: 'Answer a few questions so Aithon can recommend the right starting path.',
    icon: <HelpOutline />,
  },
];

const painOptions = [
  ['slow_response', 'Slow support response'],
  ['security_risk', 'Security risk'],
  ['no_strategy', 'No IT strategy'],
  ['tool_sprawl', 'Tool sprawl'],
  ['unclear_ownership', 'Unclear ownership'],
];

const GuidedModePanel: React.FC<GuidedModePanelProps> = ({ service, onRecommendationChange }) => {
  const defaults = useMemo(() => getGuidedModeDefaults(service), [service]);
  const [answers, setAnswers] = useState<GuidedModeAnswers>(defaults);
  const questions = useMemo(() => getGuidedQuestionsForDisplay(service), [service]);

  const recommendation = useMemo(() => evaluateGuidedMode(service, answers), [service, answers]);

  React.useEffect(() => {
    onRecommendationChange(recommendation);
  }, [onRecommendationChange, recommendation]);

  const update = (patch: Partial<GuidedModeAnswers>) => setAnswers((current) => ({ ...current, ...patch }));

  const togglePain = (key: string) => {
    const current = answers.support_pain || [];
    update({ support_pain: current.includes(key) ? current.filter((entry) => entry !== key) : [...current, key] });
  };

  const monthly = recommendation.monthlyCents ? `$${(recommendation.monthlyCents / 100).toLocaleString('en-US')}/mo` : 'Finalized after review';

  return (
    <Box sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'primary.light', borderRadius: 2, bgcolor: 'primary.50' }}>
      <Typography variant="overline" color="primary" sx={{ fontWeight: 900, letterSpacing: 1 }}>
        Guided Mode
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 900, mb: 0.75 }}>
        Choose how to configure this service
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Technical services should not become lead forms. Pick the standard package or answer a few questions and we’ll carry the recommendation into checkout.
      </Typography>

      <Grid container spacing={1.25} sx={{ mb: 2 }}>
        {pathCards.map((card) => {
          const selected = answers.path === card.path;
          return (
            <Grid item xs={12} key={card.path}>
              <Button
                fullWidth
                variant={selected ? 'contained' : 'outlined'}
                onClick={() => update({ path: card.path })}
                sx={{ justifyContent: 'flex-start', textAlign: 'left', p: 1.5, borderRadius: 2 }}
                startIcon={card.icon}
              >
                <Box>
                  <Typography sx={{ fontWeight: 900 }}>{card.title}</Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: selected ? 'primary.contrastText' : 'text.secondary' }}>{card.body}</Typography>
                </Box>
              </Button>
            </Grid>
          );
        })}
      </Grid>

      {answers.path === 'help_me_choose' && (
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          <TextField
            label="Supported users"
            type="number"
            size="small"
            value={answers.supported_users}
            onChange={(event) => update({ supported_users: Number(event.target.value) })}
            inputProps={{ min: 1, step: 1 }}
          />
          <FormControl size="small" fullWidth>
            <InputLabel>Internal IT owner?</InputLabel>
            <Select
              label="Internal IT owner?"
              value={answers.internal_it_owner || 'partial'}
              onChange={(event) => update({ internal_it_owner: event.target.value as any })}
            >
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="partial">Partial / shared</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Productivity platform</InputLabel>
            <Select
              label="Productivity platform"
              value={answers.productivity_platform || 'microsoft_365'}
              onChange={(event) => update({ productivity_platform: event.target.value })}
            >
              <MenuItem value="microsoft_365">Microsoft 365</MenuItem>
              <MenuItem value="google_workspace">Google Workspace</MenuItem>
              <MenuItem value="mixed">Mixed</MenuItem>
              <MenuItem value="not_sure">Not sure</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Security baseline</InputLabel>
            <Select
              label="Security baseline"
              value={answers.security_baseline || 'managed'}
              onChange={(event) => update({ security_baseline: event.target.value })}
            >
              <MenuItem value="basic">Basic</MenuItem>
              <MenuItem value="managed">Managed</MenuItem>
              <MenuItem value="advanced">Advanced</MenuItem>
              <MenuItem value="not_sure">Not sure</MenuItem>
            </Select>
          </FormControl>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
              Main pain points
            </Typography>
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
              {painOptions.map(([key, label]) => {
                const selected = (answers.support_pain || []).includes(key);
                return (
                  <Chip
                    key={key}
                    label={label}
                    color={selected ? 'primary' : 'default'}
                    variant={selected ? 'filled' : 'outlined'}
                    onClick={() => togglePain(key)}
                    sx={{ mb: 0.75 }}
                  />
                );
              })}
            </Stack>
          </Box>
        </Stack>
      )}

      <Alert severity={recommendation.outcome === 'signature_support_standard_package' ? 'success' : 'info'} sx={{ mb: 1.5 }}>
        <Typography sx={{ fontWeight: 900 }}>{recommendation.title}</Typography>
        <Typography variant="body2">{recommendation.summary}</Typography>
      </Alert>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2" color="text.secondary">Recommendation estimate</Typography>
        <Typography variant="body2" sx={{ fontWeight: 900 }}>{monthly}</Typography>
      </Box>
      {recommendation.reasons.length > 0 && (
        <Stack spacing={0.5}>
          {recommendation.reasons.slice(0, 3).map((reason) => (
            <Typography key={reason} variant="caption" color="text.secondary">• {reason}</Typography>
          ))}
        </Stack>
      )}
      {questions.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25 }}>
          Guided spec loaded from order schema · {questions.length} supplier-authored questions
        </Typography>
      )}
    </Box>
  );
};

export default React.memo(GuidedModePanel);
